import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { Loader2, Send, Home, RefreshCw, FileText, ImagePlus, Video } from "lucide-react";

export default function InternalCase() {
    const { token } = useParams();
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get(`/public/internal/${token}`);
            setCaseData(data);
            setNotFound(false);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (caseData?.internal_messages?.length) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [caseData?.internal_messages?.length]);

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setSending(true);
        try {
            const { data } = await api.post(`/public/internal/${token}/messages`, { text: trimmed });
            setCaseData(data);
            setText("");
            toast.success("Beskeden er sendt til Gardinbussen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke sende beskeden");
        } finally {
            setSending(false);
        }
    };

    const fileUrl = (fid) => `${API}/public/internal/${token}/files/${fid}`;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#78736E]" />
            </div>
        );
    }

    if (notFound || !caseData) {
        return (
            <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center px-6">
                    <div className="text-center">
                        <h1 className="font-heading text-2xl font-medium text-[#2C2A29] mb-2">Sag ikke fundet</h1>
                        <p className="text-sm text-[#78736E] mb-6">Linket er muligvis udløbet eller forkert.</p>
                        <Link to="/">
                            <Button variant="outline" className="border-[#D2C9B9]" data-testid="internal-home-button">
                                <Home className="w-4 h-4 mr-2" />
                                Til forsiden
                            </Button>
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    const r = caseData.recipient || {};
    const fileMeta = (fid) => (caseData.files || []).find((f) => f.id === fid) || {};
    const isVideoFile = (fid) => {
        const m = fileMeta(fid);
        return (m.content_type || "").startsWith("video/") || ["mp4","mov","m4v","webm","3gp","avi","mkv","qt"].includes(m.ext);
    };
    const isPdfFile = (fid) => {
        const m = fileMeta(fid);
        return m.ext === "pdf" || (m.content_type || "").includes("pdf");
    };

    return (
        <div className="min-h-screen bg-[#FAFAF9]" data-testid="internal-case-page">
            <Header />
            <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
                {/* Greeting */}
                <div className="mb-6 text-sm text-[#5C5854]" data-testid="internal-greeting">
                    Hej <strong>{r.name}</strong> — du er logget ind som <span className="text-[#2C2A29] font-medium">{r.role_label}</span>.
                </div>

                {/* Case header */}
                <div className="bg-white border border-[#E5E1DA] rounded-2xl p-6 sm:p-8 shadow-[0_8px_32px_rgba(44,42,41,0.04)] mb-6">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#78736E]">Sagsnummer</div>
                            <h1 className="font-heading text-3xl font-medium text-[#2C2A29] mt-1" data-testid="internal-case-number">
                                {caseData.case_number}
                            </h1>
                            <div className="text-sm text-[#78736E] mt-1">
                                Oprettet {(caseData.created_at || "").slice(0, 10)}
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <StatusBadge status={caseData.status} />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => load(true)}
                                className="text-[#78736E] hover:bg-[#F0EBE1] text-xs h-8"
                                data-testid="internal-refresh-button"
                            >
                                <RefreshCw className="w-3 h-3 mr-1.5" />
                                Opdater
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-[#E5E1DA] pt-5">
                        <Row k="Kunde" v={caseData.customer_name} />
                        <Row k="Telefon" v={caseData.customer_phone} />
                        <Row k="Adresse" v={[caseData.customer_address, [caseData.customer_postal, caseData.customer_city].filter(Boolean).join(" ")].filter(Boolean).join(", ")} />
                        <Row k="Ordrenummer" v={caseData.order_number || "-"} />
                        <Row k="Kategori" v={caseData.problem_category_label} />
                        <Row
                            k="Produkt(er)"
                            v={
                                <span>
                                    {caseData.products?.map((p, i) => (
                                        <span key={i} className="block">
                                            {p.product_type}{p.room ? ` · ${p.room}` : ""}{p.color_model ? ` · ${p.color_model}` : ""} · {p.quantity} stk.
                                        </span>
                                    ))}
                                </span>
                            }
                        />
                    </div>

                    <div className="mt-5 pt-5 border-t border-[#E5E1DA]">
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2">Kundens problembeskrivelse</div>
                        <div className="text-sm text-[#2C2A29] whitespace-pre-wrap">{caseData.problem_description}</div>
                    </div>
                </div>

                {/* Files */}
                {(caseData.file_ids?.length > 0 || caseData.receipt_file_id) && (
                    <div className="bg-white border border-[#E5E1DA] rounded-2xl p-5 mb-6 shadow-[0_8px_32px_rgba(44,42,41,0.04)]">
                        <div className="flex items-center gap-2 mb-3">
                            <ImagePlus className="w-4 h-4 text-[#4A4641]" />
                            <h2 className="font-heading text-base font-medium text-[#2C2A29]">Billeder og kvittering</h2>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {caseData.file_ids?.map((fid) => (
                                <a
                                    key={fid}
                                    href={fileUrl(fid)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="aspect-square rounded-lg overflow-hidden bg-[#F4F2EF] border border-[#E5E1DA] hover:border-[#4A4641] transition-colors flex items-center justify-center"
                                    data-testid={`internal-file-${fid}`}
                                >
                                    {isVideoFile(fid) ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center p-1 text-center bg-[#2C2A29]">
                                            <Video className="w-5 h-5 text-white" />
                                            <div className="text-[9px] text-white/80 truncate w-full mt-0.5">{fileMeta(fid).filename || "Video"}</div>
                                        </div>
                                    ) : isPdfFile(fid) ? (
                                        <FileText className="w-5 h-5 text-[#78736E]" />
                                    ) : (
                                        <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover" />
                                    )}
                                </a>
                            ))}
                            {caseData.receipt_file_id && (
                                <a
                                    href={fileUrl(caseData.receipt_file_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="aspect-square rounded-lg overflow-hidden bg-[#F0EBE1] border border-[#E5E1DA] hover:border-[#4A4641] transition-colors flex flex-col items-center justify-center"
                                    data-testid="internal-receipt-link"
                                >
                                    <FileText className="w-5 h-5 text-[#4A4641]" />
                                    <span className="text-[10px] text-[#4A4641] mt-1">Kvittering</span>
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {/* Messages */}
                <div className="bg-[#FAFAF9] border border-[#E5E1DA] rounded-2xl shadow-[0_8px_32px_rgba(44,42,41,0.04)] overflow-hidden">
                    <div className="px-5 py-3 border-b border-[#E5E1DA] bg-white">
                        <h2 className="font-heading text-base font-medium text-[#2C2A29]">Intern korrespondance</h2>
                        <div className="text-xs text-[#78736E] mt-0.5">Kun synlig for Gardinbussen og dig — ikke for kunden</div>
                    </div>
                    <div className="px-4 py-4 max-h-[60vh] overflow-y-auto">
                        {(!caseData.internal_messages || caseData.internal_messages.length === 0) && (
                            <div className="text-center py-8 text-sm text-[#78736E]">Ingen beskeder endnu — skriv den første nedenfor.</div>
                        )}
                        <AnimatePresence initial={false}>
                            {caseData.internal_messages?.map((m) => {
                                const isMine = m.sender_type !== "agent";
                                return (
                                    <motion.div
                                        key={m.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className={`flex mb-3 ${isMine ? "justify-end" : "justify-start"}`}
                                        data-testid={`internal-message-${m.id}`}
                                    >
                                        <div
                                            className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                                                isMine
                                                    ? "bg-[#4A4641] text-white rounded-br-sm"
                                                    : "bg-white border border-[#E5E1DA] text-[#2C2A29] rounded-bl-sm"
                                            }`}
                                        >
                                            <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isMine ? "text-white/60" : "text-[#78736E]"}`}>
                                                {isMine ? `Dig (${r.name})` : `${m.sender_name} · Gardinbussen`}
                                            </div>
                                            <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                            <div className={`text-[10px] mt-1.5 ${isMine ? "text-white/50" : "text-[#78736E]"}`}>
                                                {(m.created_at || "").slice(0, 16).replace("T", " ")}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                        <div ref={messagesEndRef} />
                    </div>
                    <div className="border-t border-[#E5E1DA] p-4 bg-white">
                        <Textarea
                            rows={3}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Skriv en besked til Gardinbussen..."
                            disabled={sending}
                            data-testid="internal-message-input"
                            className="resize-none border-[#E5E1DA]"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                            }}
                        />
                        <div className="flex items-center justify-between mt-2">
                            <div className="text-xs text-[#78736E]">Tryk Ctrl+Enter for at sende</div>
                            <Button
                                onClick={send}
                                disabled={sending || !text.trim()}
                                className="bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                                data-testid="internal-send-button"
                            >
                                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                Send
                            </Button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Header() {
    return (
        <header className="border-b border-[#E5E1DA] bg-white">
            <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                <Logo to="/" size="sm" subtitle={null} />
                <div className="text-xs text-[#78736E] uppercase tracking-wider">Intern portal</div>
            </div>
        </header>
    );
}

function Row({ k, v }) {
    return (
        <div className="grid grid-cols-[110px_1fr] gap-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">{k}</div>
            <div className="text-sm text-[#2C2A29]">{v}</div>
        </div>
    );
}
